import { Play } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { Skeleton } from '~/components/ui/skeleton';
import { Textarea } from '~/components/ui/textarea';

import { useSensitiveMask } from '../../hooks/use-sensitive-mask';
import {
  useRunWorkflowQuery,
  useWorkflowQueryDefinitions,
} from '../../hooks/use-workflow-inspection';
import { inspectionErrorStatus } from '../../utils/inspection-errors';
import { JsonViewer } from '../json-viewer';
import { SectionError } from '../section-error';

const MAX_ARGS = 20;

/** Parse the args box: must be a JSON array of at most 20 items, else null. */
function parseArgs(text: string): unknown[] | null {
  if (text.trim() === '') return [];
  try {
    const parsed: unknown = JSON.parse(text);
    return Array.isArray(parsed) && parsed.length <= MAX_ARGS ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Queries (§3.8/§3.9): pick one of the workflow's declared query handlers,
 * pass positional JSON args, see the decoded (masked) result. `fallback`
 * means the worker couldn't list its handlers, so only `__stack_trace` is
 * offered. 503 = no worker answered within the server's 10 s timeout.
 */
export function QueriesTab({ workflowId, runId }: { workflowId: string; runId: string | null }) {
  const { t } = useTranslation('workflow-inspection');
  const definitions = useWorkflowQueryDefinitions(workflowId, runId);
  const run = useRunWorkflowQuery(workflowId, runId);
  const { mask } = useSensitiveMask([workflowId, runId]);
  // Mutation results aren't cached queries, so mask the latest one on render.
  const queryResult = run.data ? mask(run.data) : undefined;
  const [queryName, setQueryName] = useState('');
  const [argsText, setArgsText] = useState('');

  if (definitions.isLoading)
    return <Skeleton className="h-40 w-full" data-testid="workflow-inspector-queries-loading" />;
  if (definitions.isError || !definitions.data)
    return (
      <SectionError
        onRetry={() => void definitions.refetch()}
        isRetrying={definitions.isFetching}
        testId="workflow-inspector-queries-error"
      />
    );

  const queries = definitions.data.queries;
  // Default to the first declared query until the operator picks one.
  const selected = queryName !== '' ? queryName : (queries[0]?.name ?? '');
  const args = parseArgs(argsText);
  const runError = run.error;
  const errorText =
    runError === null
      ? null
      : inspectionErrorStatus(runError) === 503
        ? t('queries.noWorker')
        : t('queries.failed', { message: runError.message });

  return (
    <div className="space-y-4" data-testid="workflow-inspector-queries">
      {definitions.data.source === 'fallback' && (
        <p
          className="bg-muted/40 rounded-md px-3 py-2 text-xs"
          data-testid="workflow-inspector-queries-fallback-banner"
        >
          {t('queries.fallbackNotice')}
        </p>
      )}
      <div className="grid gap-4 md:grid-cols-[minmax(200px,280px)_1fr]">
        <div className="space-y-1.5">
          <Label>{t('queries.query')}</Label>
          <Select value={selected} onValueChange={setQueryName}>
            <SelectTrigger
              className="cursor-pointer"
              data-testid="workflow-inspector-queries-select"
            >
              <SelectValue placeholder={t('queries.selectQuery')} />
            </SelectTrigger>
            <SelectContent data-testid="workflow-inspector-queries-select-content">
              {queries.map((q) => (
                <SelectItem
                  key={q.name}
                  value={q.name}
                  className="cursor-pointer font-mono text-xs"
                  data-testid={`workflow-inspector-queries-option-${q.name}`}
                >
                  {q.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {queries.find((q) => q.name === selected)?.description && (
            <p className="text-muted-foreground text-xs">
              {queries.find((q) => q.name === selected)?.description}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="workflow-query-args">{t('queries.args')}</Label>
          <Textarea
            id="workflow-query-args"
            data-testid="workflow-inspector-queries-args-input"
            value={argsText}
            placeholder={t('queries.argsPlaceholder')}
            className="min-h-[72px] font-mono text-xs"
            onChange={(e) => {
              setArgsText(e.target.value);
            }}
          />
          {args === null && (
            <p
              className="text-destructive text-xs"
              data-testid="workflow-inspector-queries-args-error"
            >
              {t('queries.argsInvalid')}
            </p>
          )}
        </div>
      </div>
      <Button
        type="button"
        className="cursor-pointer gap-1.5 disabled:cursor-not-allowed"
        disabled={!selected || args === null}
        isLoading={run.isPending}
        data-testid="workflow-inspector-queries-run-button"
        onClick={() => {
          if (selected && args) run.mutate({ queryName: selected, args });
        }}
      >
        <Play aria-hidden="true" className="size-4" />
        {t('queries.run')}
      </Button>

      {errorText && (
        <p className="text-destructive text-sm" data-testid="workflow-inspector-queries-run-error">
          {errorText}
        </p>
      )}
      {queryResult && (
        <section className="space-y-1.5" data-testid="workflow-inspector-queries-result">
          <h4 className="text-foreground text-sm font-semibold">
            {t('queries.result')} ·{' '}
            <span className="font-mono text-xs">{queryResult.queryName}</span>
          </h4>
          {typeof queryResult.result === 'string' ? (
            <pre className="bg-muted/30 max-h-96 overflow-auto rounded-lg border p-3 font-mono text-xs whitespace-pre">
              {queryResult.result}
            </pre>
          ) : (
            <JsonViewer
              value={queryResult.result}
              title={queryResult.queryName}
              testId="workflow-inspector-queries-result-json"
            />
          )}
        </section>
      )}
    </div>
  );
}
